import { CommonModule } from '@angular/common';
import { Component, OnDestroy, OnInit } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatCardModule } from '@angular/material/card';
import { MatNativeDateModule } from '@angular/material/core';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatDialog } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { PageEvent } from '@angular/material/paginator';
import { MatProgressSpinnerModule } from "@angular/material/progress-spinner";
import { MatSelectModule } from '@angular/material/select';
import { MatSortModule, Sort } from '@angular/material/sort';
import { MatTableDataSource, MatTableModule } from '@angular/material/table';
import { Router } from '@angular/router';
import { finalize, Subject, take } from 'rxjs';
import { ClientService } from '../../services/client.service';
import { NotificationService } from '../../services/notification.service';
import { LoadingComponent } from "../../shared/loading/loading.component";
import { ModalComponent } from '../../shared/modal/modal.component';
import { SearchBarComponent } from "../../shared/searchbar/searchbar.component";
import { SharedTableComponent } from "../../shared/shared-table/shared-table.component";

@Component({
  selector: 'app-client',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    MatTableModule,
    MatSortModule,
    MatCardModule,
    MatIconModule,
    MatInputModule,
    MatFormFieldModule,
    MatSelectModule,
    MatButtonModule,
    MatDatepickerModule,
    MatNativeDateModule,
    SharedTableComponent,
    SearchBarComponent,
    MatProgressSpinnerModule,
    LoadingComponent
],
  templateUrl: './client.component.html',
  styleUrl: './client.component.scss'
})
export class ClientComponent implements OnInit, OnDestroy {

  constructor(
    private readonly router: Router,
    private readonly dialog: MatDialog,
    private readonly clientService: ClientService,
    private readonly notificationService: NotificationService
  ) {}

  isLoading = false;
  hasError = false;
  isLoadingFullScreen = false;

  clientsListLoaded$ = new Subject<void>();

  // Stato tabella / query
  sortBy = 'name:asc';
  sortField = '';
  sortDirection = '';
  searchQuery = '';

  // Paginazione server-side
  pageIndex = 0;
  pageSize = 10;
  totalItems = 0;

  // DataSource tabella
  dataSourceClient = new MatTableDataSource<any>();

  // campi nascosti per la modale
  hiddenFieldsValues: Record<string, any> = {
    creationDate: new Date().toISOString()
  };

  ngOnInit(): void {
    this.loadClientsList(this.pageIndex, this.pageSize, this.sortBy, this.searchQuery);
  }

  loadClientsList(pageIndex: number, pageSize: number, sortBy: string, clientFilter: string) {
    this.isLoading = true;
    this.hasError = false;
    this.clientService.getClientsList(pageIndex, pageSize, sortBy, clientFilter).pipe(finalize(() => {
       this.isLoading = false;
       this.clientsListLoaded$.next(); 
      })).subscribe({
      next: (response) => {
        const content = response?.content ?? [];
        this.dataSourceClient.data = content;
        this.totalItems = response?.totalElements ?? 0;

        // allinea lo stato locale con quanto ritorna il BE
        this.pageIndex = (response?.number ?? pageIndex) ?? 0;
        this.pageSize  = (response?.size ?? pageSize) ?? this.pageSize;
      },
      error: (err) => {
        console.log('errore nel recupero dei dati', err);
        this.dataSourceClient.data = [];
        this.totalItems = 0;
        this.hasError = true;
      }
    });
  }

  // Navigazione al dettaglio
  goToDetail(row: any) {
    this.router.navigate(['/client/detail', row.id, encodeURIComponent(row.name)]);
  }

  // Filtro: riparti sempre da pagina 0
  onFilterChanged(value: any) {
    this.searchQuery = value?.name ?? '';
    this.pageIndex = 0;
    this.loadClientsList(this.pageIndex, this.pageSize, 'name:asc', this.searchQuery);
  }

  // Ordinamento: riparti sempre da pagina 0
  onSortChanged(sort: Sort) {
    this.sortField = sort.active;
    this.sortDirection = sort.direction || 'asc';
    this.sortBy = `${this.sortField}:${this.sortDirection}`;
    this.pageIndex = 0;
    this.loadClientsList(this.pageIndex, this.pageSize, this.sortBy, this.searchQuery);
  }

  // Cambio pagina / page size dal paginator (server-side)
  onPageChanged(ev: PageEvent) {
    this.pageIndex = ev.pageIndex;
    this.pageSize = ev.pageSize;
    this.loadClientsList(this.pageIndex, this.pageSize, this.sortBy, this.searchQuery);
  }

  openDialog(event: { data: any; type: 'add' | 'edit' | 'delete' }) {
    const dialogRef = this.dialog.open(ModalComponent, {
      width: '400px',
      data: {
        action: event.type,
        payload: event.data,
        visibleFields: ['name', 'description'],
        readonlyFields: {
          edit: ['name']
        },
        hiddenFields: ['creationDate'],
        hiddenFieldsValues: this.hiddenFieldsValues,
        labels: {
          name: 'Nome del client',
          description: 'Descrizione'
        }
      }
    });

    dialogRef.afterClosed().subscribe(result => {
      if (!result) return;

      this.isLoadingFullScreen = true;

      if (event.type === 'add') {
        this.clientService.addNewClient(result).pipe(finalize(() => { this.isLoadingFullScreen = false; })).subscribe({
          next: () => {
            this.loadClientsList(this.pageIndex, this.pageSize, this.sortBy, this.searchQuery);
            this.clientsListLoaded$.pipe(take(1)).subscribe(() => {
                this.notificationService.entitySuccess('add', 'Client');
            })
          },
          error: (err) => {
            console.log('errore nella richiesta', err);
            this.notificationService.entityError('add', 'Client');
          }
        });

      } else if (event.type === 'edit') {
        const index = this.dataSourceClient.data.findIndex(e => e.id === result.id);
        if (index !== -1) {
          this.clientService.editClient(result).pipe(finalize(() => { this.isLoadingFullScreen = false; })).subscribe({
            next: () => {
              this.loadClientsList(this.pageIndex, this.pageSize, this.sortBy, this.searchQuery);
              this.clientsListLoaded$.pipe(take(1)).subscribe(() => {
                this.notificationService.entitySuccess('update', 'Client');
              })
            },
            error: (err) => {
              console.log('errore nella richiesta', err);
              this.notificationService.entityError('update', 'Client');
            }
          });
        }

      } else if (event.type === 'delete') {
        this.clientService.deleteClient(result.id).pipe(finalize(() => { this.isLoadingFullScreen = false; })).subscribe({
          next: () => {
            // Se ho cancellato l’unica riga della pagina corrente e non sono alla prima pagina,
            // scala pagina per evitare di restare su una pagina vuota.
            if (this.pageIndex > 0 && this.dataSourceClient.data.length === 1) {
              this.pageIndex = this.pageIndex - 1;
            }
            this.loadClientsList(this.pageIndex, this.pageSize, this.sortBy, this.searchQuery);
            this.clientsListLoaded$.pipe(take(1)).subscribe(() => {
                this.notificationService.entitySuccess('delete', 'Client');
            })
          },
          error: (err) => {
            if(err.status !== 409) {
              console.log('errore nella richiesta', err);
              this.notificationService.entityError('delete', 'Client');
            }  
          }
        });
      }
    });
  }

  ngOnDestroy(): void {
    this.clientsListLoaded$.complete();
  }
}